import type { Fetcher } from '../api/impl/utils/Fetcher';
import { createBrowserReplicantFetch } from '../backends/browser/ReplicantBrowserBackend';
import type { PostAnalyticsFN } from '../common/Analytics';
import type { ErrorReporter } from '../common/ErrorReporter';
import logger, { LogLevel } from '../logger';
import { Replicant, ReplicantConfig } from '../ReplicantConfig';
import { ReplicantPlatform, toReplicantPlatform } from '../ReplicantPlatform';
import { createPostAmplitudeAnalytics } from '../utils/AnalyticsAmplitude';
import { ClientReplicant, CommonReplicantConfiguration, InternalReplicantOptions } from './ClientReplicant';
import { CommonReplicantOpts, createReplicant, OnLoginActionArgs } from './createReplicant';

// Helper functions to creating offline replicant clients.
export type OfflineReplicantOpts = {
    createResponseOverride?: (body: object | null, status: number, headers: string[][]) => Response;
    devOpts?: {
        /**
         * Offline mode error reporter implementation, used for example by the `reportError` action API.
         *
         * Defaults to `undefined`.
         */
        errorReporter?: ErrorReporter;

        /**
         * Offline mode analytics implementation, used for example by the `sendAnalyticsEvents` action API.
         *
         * Defaults to Amplitude analytics.
         */
        postAnalytics?: PostAnalyticsFN;
    };
    fetcher?: Fetcher;
    latency?: number;
    platform: ReplicantPlatform;
    storageKeyPrefix?: string;
    storageOverride?: Storage;
} & CommonReplicantOpts &
    CommonReplicantConfiguration;

const browserFetcher: Fetcher = {
    fetch: (request) =>
        fetch(request.url, {
            body: 'body' in request ? request.body : undefined,
            headers: request.headers,
            method: request.method,
        }).then(async (response) => {
            const headers: Record<string, string> = {};

            response.headers.forEach((value, key) => {
                headers[key] = value;
            });

            return {
                body: await response.text(),
                headers,
                statusCode: response.status,
                url: request.url,
            };
        }),
};

export async function createOfflineReplicant<T extends Replicant>(
    config: ReplicantConfig<T>,
    id: string,
    { kvStore, ...options }: OfflineReplicantOpts & OnLoginActionArgs<T>,
): Promise<ClientReplicant<T>> {
    if (options.debug) {
        logger.level(LogLevel.debug);
    }

    // Validate platform.
    toReplicantPlatform(options.platform);

    const latency = (options && options.latency) ?? 0;
    const storageOverride = options && options.storageOverride;
    const createResponseOverride = options && options.createResponseOverride;

    const fetcher = options.fetcher ?? browserFetcher;

    const replicantOpts: InternalReplicantOptions = {
        ...options,
        endpoint: 'browser://',
        signature: '',
        fetchOverride: createBrowserReplicantFetch({
            fetcher,
            platform: options.platform,
            config,
            latency,
            storageOverride,
            errorReporter: options.devOpts?.errorReporter,
            postAnalytics: options.devOpts?.postAnalytics ?? createPostAmplitudeAnalytics(fetcher),
            responseOverride: createResponseOverride,
            dateNow: options.devOpts?.dateNow,
            storageKeyPrefix: options.storageKeyPrefix,
        }),
        devOpts: options.devOpts,
    };

    const { replicant } = await createReplicant({
        config,
        id,
        options: replicantOpts,
        keyValueStorePrefetchKeys: kvStore?.prefetchKeys,
        internalKeyValueStorePrefetchKeys: kvStore?.prefetchInternalKeys,
    });

    return replicant;
}
